import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Link } from 'react-router-dom';
import { Star, MapPin, Loader2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { getCompaniesNearby } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

// Default center = geographical center of Denmark
const DK_CENTER = [56.05, 10.15];
const DK_ZOOM = 7;

// Cached icons — created once, not on every render (huge perf win)
const originIcon = L.divIcon({
  className: 'fh-map-marker fh-marker-origin',
  html: `<svg width="36" height="48" viewBox="0 0 34 46" xmlns="http://www.w3.org/2000/svg" style="display:block;">
    <path d="M17 0C7.6 0 0 7.6 0 17c0 12 17 29 17 29s17-17 17-29C34 7.6 26.4 0 17 0z"
          fill="#c97f59" stroke="#fff" stroke-width="2"/>
    <circle cx="17" cy="17" r="6" fill="#fff"/>
  </svg>`,
  iconSize: [36, 48],
  iconAnchor: [18, 48],
  popupAnchor: [0, -44],
});

// Category → distinct color + short label. Keeps pin shape consistent so it still
// reads as a "map pin" but colors + label make each trade instantly identifiable.
const CATEGORY_STYLES = {
  'Tømrer':                { color: '#8b5a2b', label: 'T'  },
  'Snedker':               { color: '#7c3f00', label: 'Sn' },
  'Murer':                 { color: '#d97706', label: 'Mu' },
  'Elektriker':            { color: '#eab308', label: 'E'  },
  'VVS':                   { color: '#0284c7', label: 'V'  },
  'Kloak':                 { color: '#1e40af', label: 'K'  },
  'Tag':                   { color: '#475569', label: 'Ta' },
  'Maler':                 { color: '#be185d', label: 'Ma' },
  'Gulve':                 { color: '#a16207', label: 'G'  },
  'Glarmester':            { color: '#0891b2', label: 'Gl' },
  'Smed':                  { color: '#525252', label: 'Sm' },
  'Anlægsgartner':         { color: '#16a34a', label: 'An' },
  'Brolægger':             { color: '#78716c', label: 'Br' },
  'Entreprenør':           { color: '#57534e', label: 'En' },
  'Isolering':             { color: '#f59e0b', label: 'Is' },
  'Ventilation & Klima':   { color: '#0d9488', label: 'Ve' },
  'Solenergi':             { color: '#f97316', label: 'So' },
  'Låsesmed & Sikring':    { color: '#ca8a04', label: 'L'  },
  'Skadeservice':          { color: '#b91c1c', label: 'Sk' },
  'Køkkenmontør':          { color: '#9a3412', label: 'Kø' },
};
const DEFAULT_STYLE = { color: '#134436', label: '·' };

const _iconCache = new Map();
const getCompanyIcon = (category) => {
  const style = CATEGORY_STYLES[category] || DEFAULT_STYLE;
  const cacheKey = `${style.color}|${style.label}`;
  if (_iconCache.has(cacheKey)) return _iconCache.get(cacheKey);
  const icon = L.divIcon({
    className: 'fh-map-marker fh-marker-company',
    html: `<svg width="34" height="46" viewBox="0 0 34 46" xmlns="http://www.w3.org/2000/svg" style="display:block;">
      <path d="M17 0C7.6 0 0 7.6 0 17c0 12 17 29 17 29s17-17 17-29C34 7.6 26.4 0 17 0z"
            fill="${style.color}" stroke="#fff" stroke-width="2"/>
      <text x="17" y="21" text-anchor="middle" fill="#fff" font-size="11" font-weight="700"
            font-family="system-ui,-apple-system,sans-serif" style="user-select:none;">${style.label}</text>
    </svg>`,
    iconSize: [34, 46],
    iconAnchor: [17, 46],
    popupAnchor: [0, -42],
  });
  _iconCache.set(cacheKey, icon);
  return icon;
};

export default function CompaniesMap({ defaultCategory = '' }) {
  const { user, loading: authLoading } = useAuth();
  const [postalCode, setPostalCode] = useState('');
  const [addressInput, setAddressInput] = useState('');
  const [radius, setRadius] = useState(50);
  const [origin, setOrigin] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const mapRef = useRef(null);

  const fetchNearby = useCallback(async (pc, r, fullAddress) => {
    setLoading(true);
    setError('');
    try {
      const params = { radius_km: r };
      if (pc) params.postal_code = pc;
      if (fullAddress) params.full_address = fullAddress;
      if (defaultCategory) params.category = defaultCategory;
      const res = await getCompaniesNearby(params);
      setCompanies(res.data.companies || []);
      if (res.data.origin?.lat && res.data.origin?.lng) {
        setOrigin({
          lat: res.data.origin.lat,
          lng: res.data.origin.lng,
          postal_code: res.data.origin.postal_code,
          address: fullAddress || '',
        });
      } else {
        setOrigin(null);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Kunne ikke hente håndværkere');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [defaultCategory]);

  // Initial load — runs once. Wait until AuthContext finishes loading so, if the
  // user is logged in, we can seed the search with their precise saved address
  // instead of falling back to a postal centroid.
  const initialLoadedRef = useRef(false);
  useEffect(() => {
    if (initialLoadedRef.current) return;
    if (authLoading) return; // wait for AuthContext to hydrate
    initialLoadedRef.current = true;
    const pc = user?.postal_code || '';
    const addr = user?.address || '';
    setPostalCode(pc);
    setAddressInput(addr);
    fetchNearby(pc, 50, addr);
  }, [fetchNearby, user, authLoading]);

  // Pan/zoom smoothly when origin changes — using imperative ref (no re-render trigger)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !origin) return;
    const targetZoom = radius <= 25 ? 10 : radius <= 75 ? 9 : radius <= 150 ? 8 : 7;
    map.flyTo([origin.lat, origin.lng], targetZoom, { duration: 0.9, easeLinearity: 0.25 });
  }, [origin, radius]);

  // Invalidate size once when mounted (fixes gray tiles when hidden on first render)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const id = setTimeout(() => map.invalidateSize(), 250);
    return () => clearTimeout(id);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(postalCode.trim())) {
      setError('Indtast et gyldigt 4-cifret postnummer');
      return;
    }
    fetchNearby(postalCode.trim(), radius, addressInput.trim());
  };

  const handleRadiusChange = (e) => {
    const newRadius = parseInt(e.target.value, 10);
    setRadius(newRadius);
    // Re-fetch with new radius if we already have an origin
    if (origin?.postal_code) {
      fetchNearby(origin.postal_code, newRadius, addressInput.trim());
    }
  };

  // Stable markers memoized so React doesn't re-create them on every render
  const companyMarkers = useMemo(() => (
    companies.map((c) => (
      <Marker key={c.id} position={[c.lat, c.lng]} icon={getCompanyIcon(c.category)}>
        <Popup>
          <div className="min-w-[220px]" data-testid={`map-popup-${c.id}`}>
            <p className="font-semibold text-base text-primary mb-1">{c.company_name}</p>
            <div className="flex items-center gap-2 mb-2">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: (CATEGORY_STYLES[c.category] || DEFAULT_STYLE).color }}
                aria-hidden
              />
              <p className="text-xs text-muted-foreground">{c.category}</p>
            </div>
            {c.trustpilot_rating > 0 && (
              <div className="flex items-center gap-1 text-xs mb-2">
                <Star className="w-3.5 h-3.5 fill-yellow-500 text-yellow-500" />
                <span className="font-medium">{c.trustpilot_rating.toFixed(1)}</span>
                <span className="text-muted-foreground">({c.review_count})</span>
              </div>
            )}
            <div className="text-xs text-muted-foreground mb-3 space-y-0.5">
              {c.address && <p>{c.address}</p>}
              <p>{c.postal_code}{c.city ? ` ${c.city}` : ''}</p>
              {c.travel_km != null ? (
                <p className="mt-1 text-foreground font-medium">
                  🚗 {c.travel_km} km via vej{c.travel_min != null ? ` · ca. ${c.travel_min} min` : ''}
                </p>
              ) : c.distance_km != null ? (
                <p className="mt-1">{c.distance_km} km væk (fugleflugt)</p>
              ) : null}
            </div>
            <div className="flex gap-2">
              <Link
                to={`/company/${c.id}`}
                className="flex-1 text-center bg-stone-100 hover:bg-stone-200 text-foreground text-xs font-medium px-3 py-1.5 rounded-md transition"
                data-testid={`map-popup-link-${c.id}`}
              >
                Se profil
              </Link>
              <Link
                to={`/company/${c.id}?book=1`}
                className="flex-1 text-center bg-primary hover:bg-primary/90 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition"
                data-testid={`map-popup-book-${c.id}`}
              >
                Book nu
              </Link>
            </div>
          </div>
        </Popup>
      </Marker>
    ))
  ), [companies]);

  return (
    <div className="rounded-2xl overflow-hidden border border-stone-200 bg-white shadow-sm" data-testid="companies-map">
      {/* Search / Filter Bar */}
      <div className="p-5 md:p-6 bg-stone-50 border-b border-stone-200">
        <form onSubmit={handleSearch} className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch">
            <div className="relative flex-1">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primary pointer-events-none" />
              <Input
                type="text"
                placeholder="Din adresse (fx Vesterbrogade 4) — valgfri for præcis placering"
                value={addressInput}
                onChange={(e) => setAddressInput(e.target.value)}
                className="pl-12 h-12 bg-white"
                data-testid="map-address-input"
              />
            </div>
            <Input
              type="text"
              inputMode="numeric"
              placeholder="Postnr."
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="h-12 bg-white sm:w-32 text-center font-medium"
              data-testid="map-postal-input"
              maxLength={4}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex items-center gap-2 bg-white rounded-md px-3 h-12 border border-input flex-1 sm:flex-none">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Radius</span>
              <select
                value={radius}
                onChange={handleRadiusChange}
                className="bg-transparent outline-none text-sm font-medium h-full pr-1 cursor-pointer flex-1"
                data-testid="map-radius-select"
              >
                <option value={10}>10 km</option>
                <option value={25}>25 km</option>
                <option value={50}>50 km</option>
                <option value={100}>100 km</option>
                <option value={200}>200 km</option>
              </select>
            </div>
            <Button type="submit" className="h-12 px-6 flex-1 sm:flex-none" data-testid="map-search-button" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Vis håndværkere
            </Button>
          </div>
        </form>
        {error && (
          <p className="text-sm text-destructive mt-3" data-testid="map-error">{error}</p>
        )}
        {!loading && !error && (
          <p className="text-sm text-muted-foreground mt-3" data-testid="map-count">
            {origin
              ? `${companies.length} håndværker${companies.length === 1 ? '' : 'e'} inden for ${radius} km fra ${origin.postal_code}`
              : `${companies.length} håndværker${companies.length === 1 ? '' : 'e'} vist på kortet`}
          </p>
        )}
      </div>

      {/* Map — MapContainer is mounted ONCE with static initial center/zoom. */}
      {/* All updates go through the imperative ref (map.flyTo, marker replacement). */}
      <div className="relative h-[520px] w-full">
        <MapContainer
          center={DK_CENTER}
          zoom={DK_ZOOM}
          scrollWheelZoom={true}
          className="h-full w-full"
          style={{ zIndex: 0, background: '#e5e3df' }}
          ref={mapRef}
          preferCanvas={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
          />

          {origin && (
            <>
              <Marker position={[origin.lat, origin.lng]} icon={originIcon}>
                <Popup>
                  <div className="text-sm min-w-[180px]" data-testid="map-origin-popup">
                    <p className="font-semibold text-primary mb-1">Din placering</p>
                    {origin.address && (
                      <p className="text-muted-foreground text-xs">{origin.address}</p>
                    )}
                    <p className="text-muted-foreground text-xs">
                      {origin.postal_code}{origin.city ? ` ${origin.city}` : ''}
                    </p>
                  </div>
                </Popup>
              </Marker>
              <Circle
                center={[origin.lat, origin.lng]}
                radius={radius * 1000}
                pathOptions={{ color: '#134436', fillColor: '#134436', fillOpacity: 0.06, weight: 1.5 }}
              />
            </>
          )}

          {companyMarkers}
        </MapContainer>
        {loading && (
          <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center z-[400] pointer-events-none">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}
      </div>
    </div>
  );
}
